import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, History, LogOut } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContact } from "@/context/ContactContext";
import { useClient } from "@/context/ClientContext";
import { LANGS } from "@/i18n/translations";
import { BRAND } from "@/data/brand";
import { LoginDialog, HistoryDialog } from "@/components/ClientArea";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { openContact } = useContact();
  const { client, login, logout } = useClient();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [access, setAccess] = useState(false);
  const [history, setHistory] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "catalog", label: t.nav.products },
    { id: "legacy", label: t.nav.legacy },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between h-16 sm:h-20">
        <button
          data-testid="logo-home"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.emblem}
            alt="DPNox Emissions Solutions"
            className="h-11 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="font-mono text-xs uppercase tracking-[0.15em] text-white/70 hover:text-white transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[color:var(--dp-orange)] after:transition-all hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-5">
          <div className="flex items-center border border-white/15 rounded-none">
            {LANGS.map((L) => (
              <button
                key={L.code}
                data-testid={`lang-${L.code}`}
                onClick={() => setLang(L.code)}
                className={`px-2.5 py-1.5 font-mono text-[11px] tracking-widest transition-colors ${
                  lang === L.code
                    ? "bg-[color:var(--dp-orange)] text-black"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {L.label}
              </button>
            ))}
          </div>

          {client ? (
            <div className="hidden sm:flex items-center gap-3">
              <button
                data-testid="header-history-btn"
                onClick={() => setHistory(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] text-black font-semibold text-sm tracking-tight transition-colors"
              >
                <History size={15} /> {t.area.history}
              </button>
              <span
                data-testid="header-user-chip"
                className="inline-flex items-center gap-2 px-3 py-2.5 border border-white/20 text-white/80 text-sm"
              >
                <User size={14} /> {client.name}
              </span>
              <button
                data-testid="header-logout-btn"
                onClick={logout}
                aria-label={t.area.logout}
                title={t.area.logout}
                className="inline-flex items-center px-3 py-2.5 border border-white/20 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <button
              data-testid="header-access-btn"
              onClick={() => setAccess(true)}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white hover:bg-white/10 font-semibold text-sm tracking-tight transition-colors"
            >
              <User size={15} /> {t.nav.access}
            </button>
          )}

          <button
            data-testid="header-contact-cta"
            onClick={() => openContact(null)}
            className="hidden sm:inline-flex items-center px-5 py-2.5 bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] text-black font-semibold text-sm tracking-tight transition-colors"
          >
            {t.nav.cta}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            onClick={() => setMobile((v) => !v)}
          >
            {mobile ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => {
                    scrollTo(l.id);
                    setMobile(false);
                  }}
                  className="text-left font-mono text-sm uppercase tracking-widest text-white/80"
                >
                  {l.label}
                </button>
              ))}
              {client ? (
                <>
                  <span
                    data-testid="mobile-user-chip"
                    className="inline-flex items-center gap-2 text-white/70 text-sm font-mono uppercase tracking-widest"
                  >
                    <User size={15} /> {client.name}
                  </span>
                  <button
                    data-testid="mobile-history-btn"
                    onClick={() => {
                      setHistory(true);
                      setMobile(false);
                    }}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-[color:var(--dp-orange)] text-black font-semibold text-sm"
                  >
                    <History size={15} /> {t.area.history}
                  </button>
                  <button
                    data-testid="mobile-logout-btn"
                    onClick={() => {
                      logout();
                      setMobile(false);
                    }}
                    className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white font-semibold text-sm"
                  >
                    <LogOut size={15} /> {t.area.logout}
                  </button>
                </>
              ) : (
                <button
                  data-testid="mobile-access-btn"
                  onClick={() => {
                    setAccess(true);
                    setMobile(false);
                  }}
                  className="mt-2 inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white font-semibold text-sm"
                >
                  <User size={15} /> {t.nav.access}
                </button>
              )}
              <button
                data-testid="mobile-contact-cta"
                onClick={() => {
                  openContact(null);
                  setMobile(false);
                }}
                className="px-5 py-3 bg-[color:var(--dp-orange)] text-black font-semibold text-sm"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LoginDialog open={access} onOpenChange={setAccess} onLogin={login} />
      <HistoryDialog open={history} onOpenChange={setHistory} />
    </motion.header>
  );
}
